import { authApi } from "@api/auth"
import { authStorage } from "@auth/storage"
import { analytics } from "@lib/analytics"
import { logger } from "@lib/logger"
import { setThemeLocal, syncThemeFromProfessor, type Theme } from "@lib/theme"
import { useQueryClient } from "@tanstack/react-query"
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"

import { syncLanguageFromProfessor } from "@/i18n"
import type { Professor } from "@/types"

interface AuthContextValue {
  professor: Professor | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  updateProfessor: (data: Partial<Professor>) => void
  setTheme: (next: Theme) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [professor, setProfessor] = useState<Professor | null>(authStorage.getProfessor)
  const queryClient = useQueryClient()

  // On bootstrap (or when the professor changes), reconcile the local theme
  // with whatever was saved on the profile. The backend is the source of
  // truth across devices; localStorage is the cross-reload cache.
  useEffect(() => {
    syncThemeFromProfessor(professor?.settings?.theme)
  }, [professor?.settings?.theme])

  useEffect(() => {
    syncLanguageFromProfessor(professor?.settings?.language)
  }, [professor?.settings?.language])

  // Depends on the whole professor object on purpose: `identifyProfessor`
  // is idempotent and only talks to PostHog when the person properties
  // actually changed, so this one effect keeps them current across every
  // path that mutates the professor — bootstrap from storage,
  // `updateProfessor` (a renamed professor, a new language) and the
  // `/auth/me` refresh below (an `emails_blocked` flip).
  useEffect(() => {
    if (professor) {
      analytics.identifyProfessor(professor)
    } else {
      analytics.reset()
    }
  }, [professor])

  // Refresh the professor snapshot at sensible cadences so a backend
  // env-flip (e.g. MATERIAL_DELIVERY_ENABLED toggled via cloud run,
  // a future per-professor flag changed by an admin) reaches the SPA
  // without a forced logout. Two triggers:
  //
  //   - visibility change → user came back to the tab. Cheapest "maybe
  //     something changed" signal.
  //   - 5-minute interval → upper bound for tabs that stay focused
  //     all day.
  //
  // Both gate on a non-null professor so we don't poll /api/auth/me
  // on the /login or /encuesta surfaces. On error: silently log and
  // leave the cached state alone — the axios 401-refresh interceptor
  // handles the real auth-loss case.
  useEffect(() => {
    if (!professor) return

    const refresh = async () => {
      // A laptop waking up or a background tab has no network yet: firing
      // anyway was most of the "/auth/me failed" noise in PostHog (2026-10,
      // durations of 8 ms, or of 59 hours across a sleep). The `online`
      // listener below catches up once the network is back.
      if (!navigator.onLine || document.visibilityState !== "visible") return
      try {
        const fresh = await authApi.me()
        setProfessor((prev) => {
          if (!prev) return prev // logged out mid-flight
          // Only merge — keep any local-optimistic fields (e.g. theme
          // applied via setTheme before the backend round-trip).
          const merged: Professor = { ...prev, ...fresh }
          authStorage.updateProfessor(merged)
          return merged
        })
      } catch (error: unknown) {
        logger.warning("auth.me_refresh_failed", { error: String(error) })
      }
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh()
    }
    const onOnline = () => void refresh()
    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("online", onOnline)

    // 5 min — long enough not to be noisy in dev tools, short enough
    // for an env-flip to propagate within one cup-of-coffee window.
    const intervalId = window.setInterval(refresh, 5 * 60 * 1000)

    return () => {
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("online", onOnline)
      window.clearInterval(intervalId)
    }
    // Effect depends on identity-only (login/logout), NOT on the
    // professor object — re-binding on every minor field change
    // would churn the listener + reset the interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professor?.id])

  const login = useCallback(async (email: string, password: string) => {
    const data = await authApi.login(email, password)
    authStorage.set(data)
    setProfessor(data.professor)
    // Explicit, although the effect above would also do it: the effect runs
    // after render, and `professor_logged_in` must land on the identified
    // person rather than on the anonymous visitor who typed the password.
    analytics.identifyProfessor(data.professor)
    analytics.track("professor_logged_in")
  }, [])

  const logout = useCallback(() => {
    // Strictly BEFORE `reset()`: afterwards PostHog has dropped the identity
    // and the event would be attributed to a fresh anonymous visitor.
    analytics.track("professor_logged_out")
    analytics.reset()
    authStorage.clear()
    setProfessor(null)
    // Drop every cached query so the next professor never sees the previous
    // tenant's data flash before refetch (security.md §1 — tenant isolation).
    queryClient.clear()
    logger.info("auth.logout")
  }, [queryClient])

  const updateProfessor = useCallback((data: Partial<Professor>) => {
    setProfessor((prev) => {
      if (!prev) return null
      const next = { ...prev, ...data }
      authStorage.updateProfessor(next)
      return next
    })
  }, [])

  const setTheme = useCallback(
    (next: Theme) => {
      // Optimistic local apply so the UI flips immediately.
      setThemeLocal(next)
      const merged: Professor | null = professor
        ? {
            ...professor,
            settings: { ...(professor.settings ?? {}), theme: next },
          }
        : null
      if (merged) {
        setProfessor(merged)
        authStorage.updateProfessor(merged)
        // Persist to the backend; if the call fails, the local theme stays
        // applied — best-effort sync, not a blocking write.
        authApi.updateMe({ settings: merged.settings }).catch((error: unknown) => {
          logger.warning("theme.sync_failed", { theme: next, error: String(error) })
        })
      }
    },
    [professor],
  )

  return (
    <AuthContext.Provider
      value={{
        professor,
        isAuthenticated: !!professor,
        login,
        logout,
        updateProfessor,
        setTheme,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>")
  return ctx
}
